import { useRef, useState } from "react";
import { createWorkspace, createWorkspaceInvitation, friendlyMessage, type GetToken } from "../../controllers/accountApi";
import type { Workspace, WorkspaceRole } from "../../models/account";
import { useModalDismiss } from "../../controllers/useModalDismiss";

const SIZE_OPTIONS = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"];
const INDUSTRIES = [
  "의료기기", "제약/바이오", "소프트웨어", "반도체", "자동차", "제조", "헬스케어", "금융", "항공/방산", "기타",
];

type InviteRow = { email: string; role: WorkspaceRole };

// A Jira/eQMS-style workspace registration dialog: workspace basics + organization info + invite
// members in one flow. On submit it creates the workspace, then fires an invitation per member row,
// and hands the created workspace back to the caller (which selects it). The workspace key/slug is
// derived server-side from the name — it is an internal identifier and never shown here.
export function CreateWorkspaceWizard({
  getToken, onClose, onCreated,
}: {
  getToken: GetToken;
  onClose: () => void;
  onCreated: (ws: Workspace, failedInvites: string[]) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [industry, setIndustry] = useState("");
  const [size, setSize] = useState("");
  const [invites, setInvites] = useState<InviteRow[]>([{ email: "", role: "MEMBER" }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  useModalDismiss(cardRef, onClose, submitting);

  function setInvite(i: number, patch: Partial<InviteRow>) {
    setInvites((cur) => cur.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  }
  function addInvite() {
    setInvites((cur) => [...cur, { email: "", role: "MEMBER" }]);
  }
  function removeInvite(i: number) {
    setInvites((cur) => (cur.length === 1 ? cur : cur.filter((_, idx) => idx !== i)));
  }

  async function submit() {
    if (!name.trim()) { setError("워크스페이스 이름을 입력하세요."); return; }
    setSubmitting(true);
    setError(null);
    try {
      const ws = await createWorkspace(getToken, {
        name: name.trim(),
        description: description.trim() || undefined,
        industry: industry.trim() || undefined,
        size: size || undefined,
      });
      const failed: string[] = [];
      for (const inv of invites) {
        const email = inv.email.trim();
        if (!email) continue;
        try {
          await createWorkspaceInvitation(getToken, ws.id, { email, role: inv.role, expiresInDays: 7 });
        } catch {
          failed.push(email);
        }
      }
      onCreated(ws, failed);
    } catch (e) {
      setError(friendlyMessage(e) || "워크스페이스를 만들지 못했습니다.");
      setSubmitting(false);
    }
  }

  return (
    <div className="wz-overlay" role="dialog" aria-modal="true" aria-label="새 워크스페이스 등록" onClick={() => { if (!submitting) onClose(); }}>
      <div className="wz-card" ref={cardRef} onClick={(e) => e.stopPropagation()}>
        <div className="wz-head">
          <h2>새 워크스페이스 등록</h2>
          <button className="wz-x" aria-label="닫기" onClick={onClose} disabled={submitting}>✕</button>
        </div>

        <div className="wz-body">
          {error && <div className="wz-error" role="alert">{error}</div>}

          <section className="wz-section">
            <h3>기본 정보</h3>
            <label className="wz-field">
              <span className="wz-label">워크스페이스 이름 *</span>
              <input className="wz-input" autoFocus maxLength={200} value={name} onChange={(e) => setName(e.target.value)} placeholder="예: 컴패스 품질본부" />
            </label>
            <label className="wz-field">
              <span className="wz-label">설명</span>
              <textarea className="wz-input" rows={2} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="이 워크스페이스의 목적/범위를 적어주세요." />
            </label>
          </section>

          <section className="wz-section">
            <h3>조직 정보</h3>
            <div className="wz-row2">
              <label className="wz-field">
                <span className="wz-label">업종</span>
                <input className="wz-input" list="wz-industries" maxLength={100} value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="예: 의료기기" />
                <datalist id="wz-industries">
                  {INDUSTRIES.map((v) => <option key={v} value={v} />)}
                </datalist>
              </label>
              <label className="wz-field">
                <span className="wz-label">조직 규모</span>
                <select className="wz-input" value={size} onChange={(e) => setSize(e.target.value)}>
                  <option value="">선택 안 함</option>
                  {SIZE_OPTIONS.map((v) => <option key={v} value={v}>{v}명</option>)}
                </select>
              </label>
            </div>
          </section>

          <section className="wz-section">
            <h3>멤버 초대 <span className="wz-hint">(선택 — 나중에 초대해도 됩니다)</span></h3>
            {invites.map((row, i) => (
              <div className="wz-invite-row" key={i}>
                <input
                  className="wz-input"
                  type="email"
                  value={row.email}
                  onChange={(e) => setInvite(i, { email: e.target.value })}
                  placeholder="member@company.com"
                />
                <select className="wz-input wz-role" value={row.role} onChange={(e) => setInvite(i, { role: e.target.value as WorkspaceRole })}>
                  <option value="MEMBER">멤버</option>
                  <option value="ADMIN">관리자</option>
                </select>
                <button className="wz-icon-btn" aria-label="초대 행 삭제" onClick={() => removeInvite(i)} disabled={invites.length === 1}>✕</button>
              </div>
            ))}
            <button className="wz-add-invite" onClick={addInvite}>＋ 멤버 추가</button>
          </section>
        </div>

        <div className="wz-foot">
          <button className="btn ghost" style={{ width: "auto" }} onClick={onClose} disabled={submitting}>취소</button>
          <button className="btn primary" style={{ width: "auto" }} onClick={submit} disabled={submitting || !name.trim()}>
            {submitting ? "만드는 중…" : "워크스페이스 만들기"}
          </button>
        </div>
      </div>
    </div>
  );
}
